import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useEffect, useState } from 'react';
import { DeleteRuleDialog } from '../components/DeleteRuleDialog.jsx';
import { RuleEditor } from '../components/RuleEditor.jsx';
import { RuleTable } from '../components/RuleTable.jsx';
import { ApiError, templates } from '../lib/api.js';
import type { Rule, Template } from '../lib/types.js';
import './TemplatesPage.css';

export function TemplatesPage() {
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [editingRule, setEditingRule] = useState<Rule | 'new' | null>(null);
  const [deletingRule, setDeletingRule] = useState<Rule | null>(null);

  const list = useQuery<{ templates: Template[] }>({
    queryKey: ['admin', 'templates'],
    queryFn: () => templates.list(),
  });

  // Alla prima apertura si seleziona il template di default: è quello su cui si
  // interviene nella maggior parte dei casi.
  useEffect(() => {
    if (selectedId !== null || !list.data) return;
    const preferito = list.data.templates.find((t) => t.isDefault) ?? list.data.templates[0];
    if (preferito) setSelectedId(preferito.id);
  }, [list.data, selectedId]);

  const selected = list.data?.templates.find((t) => t.id === selectedId) ?? null;

  const rules = useQuery<{ rules: Rule[] }>({
    queryKey: ['admin', 'templates', selectedId, 'rules'],
    queryFn: () => templates.rules(selectedId!),
    enabled: selectedId !== null,
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'templates'] });
  };

  const removeTemplate = useMutation({
    mutationFn: (id: number) => templates.remove(id),
    onSuccess: () => {
      setSelectedId(null);
      refresh();
    },
  });

  return (
    <div className="tpl">
      <div className="tpl__head">
        <div>
          <h1>Template di permessi</h1>
          <p className="tpl__lead">
            Un template è un insieme di regole applicate a tutte le organizzazioni che lo usano.
            Le regole si valutano dalla più specifica: <strong>vince una sola regola</strong>, e
            quella decide tutti i permessi. Chi non fa match non ottiene niente.
          </p>
        </div>
        <NewTemplate onCreated={refresh} />
      </div>

      <div className="tpl__layout">
        <aside className="card tpl__list" aria-label="Template">
          {list.isPending ? (
            <p className="tpl__state">Caricamento…</p>
          ) : list.isError ? (
            <div className="alert alert--error">
              {list.error instanceof ApiError ? list.error.message : 'Caricamento non riuscito.'}
            </div>
          ) : (
            <ul>
              {list.data.templates.map((t) => (
                <li key={t.id}>
                  <button
                    type="button"
                    className={t.id === selectedId ? 'is-active' : undefined}
                    onClick={() => setSelectedId(t.id)}
                  >
                    <strong>{t.name}</strong>
                    <span>
                      {t.regole} regol{t.regole === 1 ? 'a' : 'e'} · {t.organizzazioni} organizz.
                      {t.isDefault ? ' · default' : ''}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        <section className="card tpl__rules">
          {selected === null ? (
            <p className="tpl__state">Scegli un template dall'elenco.</p>
          ) : (
            <>
              <header className="tpl__rules-head">
                <div>
                  <h2>{selected.name}</h2>
                  {selected.description ? <p>{selected.description}</p> : null}
                </div>
                <div className="tpl__rules-actions">
                  <button
                    type="button"
                    className="btn"
                    onClick={() => setEditingRule('new')}
                  >
                    Nuova regola
                  </button>
                  {selected.organizzazioni === 0 && !selected.isDefault ? (
                    <button
                      type="button"
                      className="btn btn--ghost"
                      onClick={() => removeTemplate.mutate(selected.id)}
                    >
                      Elimina template
                    </button>
                  ) : null}
                </div>
              </header>

              {removeTemplate.error ? (
                <div className="alert alert--error">
                  {removeTemplate.error instanceof ApiError
                    ? removeTemplate.error.message
                    : 'Eliminazione non riuscita.'}
                </div>
              ) : null}

              {rules.isPending ? (
                <p className="tpl__state">Caricamento regole…</p>
              ) : rules.isError ? (
                <div className="alert alert--error">Impossibile leggere le regole.</div>
              ) : rules.data.rules.length === 0 ? (
                <p className="tpl__state">
                  Nessuna regola: le organizzazioni con questo template non vedono nulla.
                </p>
              ) : (
                <RuleTable
                  rules={rules.data.rules}
                  onEdit={setEditingRule}
                  onDelete={setDeletingRule}
                />
              )}
            </>
          )}
        </section>
      </div>

      {deletingRule && selectedId !== null ? (
        <DeleteRuleDialog
          target={{ scope: 'template', templateId: selectedId, label: selected?.name ?? 'Template' }}
          rule={deletingRule}
          onClose={() => setDeletingRule(null)}
          onDeleted={() => {
            setDeletingRule(null);
            refresh();
          }}
        />
      ) : null}

      {editingRule && selectedId !== null ? (
        <RuleEditor
          target={{ scope: 'template', templateId: selectedId, label: selected?.name ?? 'Template' }}
          rule={editingRule === 'new' ? null : editingRule}
          onClose={() => setEditingRule(null)}
          onSaved={() => {
            setEditingRule(null);
            refresh();
          }}
        />
      ) : null}
    </div>
  );
}

function NewTemplate({ onCreated }: { onCreated: () => void }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const create = useMutation({
    mutationFn: () => templates.create(name.trim(), description.trim() || null),
    onSuccess: () => {
      setName('');
      setDescription('');
      setOpen(false);
      onCreated();
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (name.trim()) create.mutate();
  };

  if (!open) {
    return (
      <button type="button" className="btn" onClick={() => setOpen(true)}>
        Nuovo template
      </button>
    );
  }

  return (
    <form className="card tpl__new" onSubmit={submit}>
      <input
        className="input"
        type="text"
        value={name}
        placeholder="Nome del template"
        onChange={(e) => setName(e.target.value)}
        autoFocus
      />
      <input
        className="input"
        type="text"
        value={description}
        placeholder="Descrizione (facoltativa)"
        onChange={(e) => setDescription(e.target.value)}
      />
      {create.error ? (
        <div className="alert alert--error">
          {create.error instanceof ApiError ? create.error.message : 'Creazione non riuscita.'}
        </div>
      ) : null}
      <div className="tpl__new-actions">
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => setOpen(false)}>
          Annulla
        </button>
        <button className="btn btn--sm" type="submit" disabled={!name.trim() || create.isPending}>
          Crea
        </button>
      </div>
    </form>
  );
}
